import "./ui/theme.css";
import "./app.css";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { Toaster } from "@/components/ui/sonner";
import { applyDocumentLocale, initialLocale } from "./i18n/detect";
import { bootLocale } from "./i18n/load";
import { installBackNavigation } from "./lib/back-navigation";
import { isMiniApp, peekTelegramLaunch, takeBootstrapToken } from "./lib/bootstrap-token";
import { quietLoadInFlight } from "./lib/preload";
import { queryClient } from "./lib/query-client";
import { isOverlayPath } from "./lib/sw-routes";
import { router } from "./router";

// Grab `#k=…` and strip it from the URL before anything renders. The token is
// held in memory only, traded for an HttpOnly cookie by the first gated
// surface, and then forgotten: it never touches localStorage.
takeBootstrapToken();
// Install only after credential fragments have been consumed and stripped.
if (!isOverlayPath(window.location.pathname)) installBackNavigation(router.history);

// Inside Telegram (V3 Phase 1) the launch in the fragment is traded for the
// account cookie at once, even on a public page that would never ask for a
// session. The session library is loaded lazily for it, so a QR landing
// pays nothing: the import expression is the whole cost to the main chunk.
if (peekTelegramLaunch() !== null) {
  void import("./lib/session").then((m) => m.ensureSession());
}
if (isMiniApp()) void import("./lib/mini-app").then((m) => m.installMiniApp(router.history));

/**
 * A tab that outlives a deploy holds an index whose lazy chunks no longer
 * exist: the next tab switch tries to import a hashed file the new build
 * replaced, and the router surfaces "Failed to fetch dynamically imported
 * module" (the super console's overlay tab, the board's menu - any lazy
 * route). Vite announces exactly this as `vite:preloadError`; a reload picks
 * up the new index and the same navigation then succeeds. Guarded to once
 * per half-minute so a genuinely broken asset (offline, a half-published
 * deploy) degrades to the error instead of a reload loop.
 */
window.addEventListener("vite:preloadError", (event) => {
  // A warm-up nobody asked for (`src/lib/preload.ts`) failing in a dead zone
  // is not a stale tab: leave it to fail quietly and be retried on the tap,
  // rather than reloading a page nobody touched (and spending this guard).
  if (quietLoadInFlight()) return;
  const at = Number(sessionStorage.getItem("chunk-reload-at") ?? 0);
  if (Date.now() - at < 30_000) return;
  sessionStorage.setItem("chunk-reload-at", String(Date.now()));
  event.preventDefault();
  window.location.reload();
});

// The OBS Browser Source gets nothing it did not ask for. A toast host is an
// empty fixed-position element until something fires, but "empty" is not the
// same promise as "absent", and the overlay's promise is that this migration
// cannot reach it. It is also never navigated to or from (OBS opens the URL
// and leaves it), so reading the path once at mount is the whole story. The
// predicate is the service worker's own, so the two can never disagree.
const isOverlay = isOverlayPath(window.location.pathname);

/**
 * Nothing renders until the chosen locale's dictionary is in memory.
 *
 * For Ukrainian (the default, and what a spectator scanning a QR code gets)
 * that resolves synchronously, because `uk/core` is a static import in the main
 * chunk. English costs one small fetch here, which is a slightly later first
 * paint rather than a flash of the wrong language.
 *
 * A TOURNAMENT'S own default language is adopted by the `/t/$slug` layout
 * (`pages/TournamentLayout.tsx`), once it knows which tournament this is:
 * the front page has no single event to take a default from.
 */
/**
 * Install the service worker (ROADMAP §5, "PWA board"). `src/sw.ts` is the
 * worker; this is the whole of its footprint in the app's main chunk.
 *
 * NOT on the overlay, and that is not merely an optimisation: OBS renders
 * `/t/<slug>/overlay/:field` on stream, and it must never be able to acquire a
 * worker that could answer it from a cache. The worker refuses that URL on its
 * own too (`lib/sw-routes.ts`) — this is the same rule, one step earlier.
 *
 * `updateViaCache: "none"` so a deploy is picked up on the next load rather
 * than whenever an HTTP cache decides. Production only: the worker is built
 * from `dist`, and `bun run dev:ui` on :5173 has no build to serve.
 */
function installWorker(): void {
  if (isOverlay || !import.meta.env.PROD || !("serviceWorker" in navigator)) return;
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/sw.js", { updateViaCache: "none" }).catch(() => {
      /* an unsupported or locked-down browser still gets the whole app */
    });
  });
}

async function start(): Promise<void> {
  const locale = initialLocale();
  await bootLocale(locale);
  applyDocumentLocale(locale);
  installWorker();

  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        {!isOverlay && <Toaster />}
      </QueryClientProvider>
    </StrictMode>,
  );
}

void start();
